import { getCurrentInstance } from 'vue';

/**
 * Composable for handling i18n patching with project-specific terms
 * @param {Object} translations - Object containing translation files for different locales
 * @param {Object} translations.en - English translations
 * @param {Object} translations.fr - French translations
 * @param {Object} translations.nl - Dutch translations
 * @returns {Object} Object with patchI18nTranslations function
 */
export default function useI18nProjectPatching(translations = {}, instance = getCurrentInstance()) {
    if (!instance) {
        console.warn('useI18nProjectPatching must be called within a Vue component setup function');
        return {};
    }

    async function getProjectTerms() {
        try {
            const response = await fetch('/i18nProjectTerms.json');
            if (!response.ok) throw new Error('No config');
            return response.json();
        } catch (e) {
            return new Promise((reject) => {
                reject('No project terms config found, using default translations.');
            });
        }
    }

    // Async patcher for global i18n instance
    const patchI18nWithProjectTerms = async () => {
        try {
            const projectsTerms = await getProjectTerms();

            if (projectsTerms.en && translations.en) {
                instance.setLocaleMessage('en', replaceProjectTerms(translations.en, projectsTerms.en));
            }
            if (projectsTerms.nl && translations.nl) {
                instance.setLocaleMessage('nl', replaceProjectTerms(translations.nl, projectsTerms.nl));
            }
            if (projectsTerms.fr && translations.fr) {
                instance.setLocaleMessage('fr', replaceProjectTerms(translations.fr, projectsTerms.fr));
            }
        } catch (e) {
            // No config or error, do nothing
            console.warn('No project terms config found, using default translations.');
        }
    };

    /**
     * Patch i18n translations with project-specific terms
     * @param {Array<string>} locales - Array of locale codes to patch (defaults to ['en', 'fr', 'nl'])
     */
    const patchI18nTranslations = async (locales = ['en', 'fr', 'nl']) => {
        const messages = instance.proxy.$i18n.messages;
        try {
            const patchPromises = locales.map(async (locale) => {
                if (translations[locale]) {
                    const projectsTerms = await getProjectTerms();

                    const patchedTranslations = replaceProjectTerms(translations[locale], projectsTerms[locale]);
                    return { locale, translations: patchedTranslations };
                }
                return null;
            });

            const results = await Promise.all(patchPromises);
            console.log('Patching i18n translations with project-specific terms...', results);
            results.forEach((result) => {
                if (result && instance.proxy.$i18n) {
                    //add to existing messages
                    instance.proxy.$i18n.setLocaleMessage(result.locale, {
                        ...messages[result.locale],
                        ...result.translations,
                    });
                }
            });
            //console.log('Successfully patched i18n translations for locales:', locales);
        } catch (error) {
            console.warn('Failed to patch i18n translations:', error);
            throw error;
        }
    };

    return {
        patchI18nWithProjectTerms,
        patchI18nTranslations,
    };
}

// Utility to recursively replace terms
function replaceProjectTerms(obj, replacements) {
    if (!replacements) return obj;
    if (typeof obj === 'string') {
        let result = obj;
        for (const [from, to] of Object.entries(replacements)) {
            const regex = new RegExp(`\\b${from}\\b`, 'g');
            if (regex.test(result)) {
                result = result.replace(regex, to);
            }
        }
        return result;
    }
    if (Array.isArray(obj)) {
        return obj.map((item) => replaceProjectTerms(item, replacements));
    }
    if (typeof obj === 'object' && obj !== null) {
        const newObj = {};
        for (const key in obj) {
            newObj[key] = replaceProjectTerms(obj[key], replacements);
        }
        return newObj;
    }
    return obj;
}
